'use client';

import { ColumnDef } from '@tanstack/react-table';
import { CalendarIcon, CircleDashed, Package, Store as StoreIcon, Ellipsis } from 'lucide-react';
import { Link } from 'react-router-dom';

import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header';
import { DataTableCheckbox } from '@/components/data-table/data-table-checkbox';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { CopyableResourceBadge } from '@/components/ui/copyable-resource-badge';
import { DateDisplay } from '@/components/date-display';
import { Store } from '@/lib/api/stores';
import { STORE_ROUTES } from '@/config/router';

interface GetStoresTableColumnsProps {
  t: (key: string) => string;
  orgSlug: string;
}

export const getStoresTableColumns = ({
  t,
  orgSlug,
}: GetStoresTableColumnsProps): ColumnDef<Store>[] => {
  return [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && 'indeterminate')}
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label={t('table.selectAll')}
          className="translate-y-0.5"
        />
      ),
      cell: ({ row }) => (
        <DataTableCheckbox row={row} />
      ),
      enableSorting: false,
      enableHiding: false,
      size: 40,
    },
    {
      id: 'name',
      accessorKey: 'name',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.storeName')} />,
      cell: ({ row }) => {
        const store = row.original;
        return (
          <div className="flex flex-col gap-0.5">
            <div className="text-sm font-medium">{store.name}</div>
          </div>
        );
      },
      meta: {
        label: t('table.columns.storeName'),
        placeholder: t('searchStores'),
        variant: 'text',
        icon: () => <StoreIcon />,
      },
      enableColumnFilter: true,
    },
    {
      id: 'type',
      accessorKey: 'type',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.type')} />,
      cell: ({ row }) => {
        const store = row.original;
        return (
          <Badge variant="gray" className="min-w-[72px] justify-center capitalize">
            {t(`table.types.${store.type}`)}
          </Badge>
        );
      },
      meta: {
        label: t('table.columns.storeType'),
        variant: 'multiSelect',
        options: [
          { label: t('table.types.shopify'), value: 'shopify' },
          { label: t('table.types.woocommerce'), value: 'woocommerce' },
          { label: t('table.types.custom'), value: 'custom' },
        ],
        icon: () => <CircleDashed />,
      },
      enableColumnFilter: true,
    },
    {
      id: 'baseCurrency',
      accessorKey: 'baseCurrency',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.baseCurrency')} />,
      cell: ({ row }) => {
        const store = row.original;
        return (
          <Badge variant="gray" className="min-w-[56px] justify-center font-mono">
            {store.baseCurrency.toUpperCase()}
          </Badge>
        );
      },
      meta: {
        label: t('table.columns.baseCurrency'),
        variant: 'multiSelect',
        icon: () => <Package />,
      },
      enableColumnFilter: false,
      enableSorting: false,
    },
    {
      id: 'currencies',
      accessorKey: 'presentmentCurrencies',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.currencies')} />,
      cell: ({ row }) => {
        const store = row.original;
        const currencyCount = store.presentmentCurrencies?.length || 0;
        return (
          <div className="text-sm">
            {currencyCount} {currencyCount === 1 ? t('table.columns.currency') : t('table.columns.currencyPlural')}
          </div>
        );
      },
      enableColumnFilter: false,
      enableSorting: false,
    },
    {
      id: 'productsCount',
      accessorKey: 'productsCount',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.products')} />,
      cell: ({ row }) => {
        const store = row.original;
        return (
          <div className="text-center">{store.productsCount || 0}</div>
        );
      },
      meta: {
        label: t('table.columns.productsCount'),
        variant: 'number',
        icon: () => <Package />,
      },
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      id: 'createdAt',
      accessorKey: 'createdAt',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.created')} />,
      cell: ({ row }) => (
        <DateDisplay value={row.original.createdAt} className="text-sm" />
      ),
      meta: {
        label: t('table.columns.createdDate'),
        variant: 'dateRange',
        icon: () => <CalendarIcon />,
      },
      enableColumnFilter: true,
    },
    {
      id: 'id',
      accessorKey: 'id',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.storeId')} />,
      cell: ({ row }) => {
        const store = row.original;
        const displayValue = `${store.id.substring(0, 8)}...`;
        return (
          <CopyableResourceBadge
            name={t('table.columns.storeId')}
            value={displayValue}
            copyValue={store.id}
          />
        );
      },
      enableSorting: false,
      enableHiding: true,
      size: 140,
    },
    {
      id: 'actions',
      cell: ({ row }) => {
        const store = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                aria-label={t('table.openMenu')}
                variant="ghost"
                className="data-[state=open]:bg-muted flex h-8 w-8 p-0"
              >
                <Ellipsis className="h-4 w-4" aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[160px]">
              <DropdownMenuItem asChild>
                <Link to={STORE_ROUTES(orgSlug, store.id).STORE_BASE()}>{t('table.columns.viewDetails')}</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to={STORE_ROUTES(orgSlug, store.id).STORE_EDIT()}>{t('table.columns.editStore')}</Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
      enableSorting: false,
      enableHiding: false,
      size: 40,
    },
  ];
};
